/**
 * The "your district turned on a math curriculum" campaign. "Turned on" means the LaunchDarkly
 * `extra-curricula` flag lists a Math curriculum for this teacher — the same flag that unlocks it
 * in Align → Curriculum. Diffit has no record of when a district was turned on, so each teacher's
 * run is counted from their first view of the card.
 *
 * Kept free of service imports: the registry reads these, and a static import of the API client
 * would pull Clerk's module-scope init into everything that reads an ad id.
 */

export const CURRICULUM_LEARN_MORE_URL = 'https://web.diffit.me/whats-new/math-curriculum';

// Flat top-level seen_nux_json keys holding `{ seen_at }`, one per audience.
export const TEACHER_FIRST_SEEN_KEY = 'curriculum_announcement_first_seen_2026';
export const ADMIN_FIRST_SEEN_KEY = 'curriculum_announcement_admin_first_seen_2026';

const WINDOW_MS = 28 * 24 * 60 * 60 * 1000;

/** `[low, high]` for "K–12", "5-8", "K" or "7" (K is 0), or null when it names no grade. */
export const parseGradeRange = (label) => {
  if (typeof label !== 'string') return null;
  const [low, high = low] = label
    .split(/\s*[–-]\s*/)
    .map((grade) => (grade.trim().toUpperCase() === 'K' ? 0 : Number.parseInt(grade, 10)));
  return Number.isNaN(low) || Number.isNaN(high) ? null : [low, high];
};

/** Whether the teacher's grades overlap the curriculum's. Unknown on either side counts as yes. */
export const teachesCurriculumGrades = (diffitUser, curriculumGrades) => {
  const range = parseGradeRange(curriculumGrades);
  const teacherRanges = (diffitUser?.grade_levels || [])
    .map((entry) => parseGradeRange(entry?.grade_level))
    .filter(Boolean);
  if (!range || !teacherRanges.length) return true;
  return teacherRanges.some(([low, high]) => low <= range[1] && high >= range[0]);
};

/** The first Math curriculum in `extraIds` per the published index, or null. */
export const pickAnnouncedCurriculum = (index, extraIds) => {
  const entry = (index || []).find(
    (curriculum) => curriculum.subject === 'Math' && extraIds?.includes(curriculum.id)
  );
  return entry
    ? { id: entry.id, name: entry.display_name || entry.name, grades: entry.grades }
    : null;
};

/** Whether the card is still inside its 28-day run. Never seen means it is. */
export const isWithinAnnouncementWindow = (seenNux, key, now = Date.now()) => {
  const seenAt = Date.parse(seenNux?.[key]?.seen_at ?? '');
  return Number.isNaN(seenAt) || now - seenAt < WINDOW_MS;
};

// Function-valued descriptor fields, resolved per teacher by resolveAdFields.

// No name only in a `?previewAd=` view.
export const curriculumName = (ctx) => ctx.announcedCurriculumName || 'a new math curriculum';

// The workshop falls back to the Align menu for an id this teacher can't open.
export const curriculumLessonHref = (ctx) =>
  `/?open=curriculum&curriculum=${encodeURIComponent(ctx.announcedCurriculumId ?? '')}`;

export const adminShareMessage = (ctx) =>
  [
    'Hi all,',
    '',
    `Good news: ${curriculumName(ctx)} is now available in Diffit. When you create a resource, open Align, choose Curriculum, and pick the lesson you're teaching. Diffit will build materials that match it.`,
    '',
    `Learn more: ${CURRICULUM_LEARN_MORE_URL}`,
    'Open Diffit: https://app.diffit.me',
  ].join('\n');

export const adminShareMailto = (ctx) =>
  `mailto:?subject=${encodeURIComponent(`${curriculumName(ctx)} is now in Diffit`)}&body=${encodeURIComponent(adminShareMessage(ctx))}`;
